import { useMutation } from "@apollo/client";
import {
  ActionIcon,
  Button,
  Grid,
  Group,
  LoadingOverlay,
  ScrollArea,
  Tabs,
  TextInput,
} from "@mantine/core";
import { IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconUpload } from "@tabler/icons-react";
import { CREATE_CATEGORY } from "apollo/mutuations";
import { GET_CATEGORIES } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { tabList } from "components/utilities/tablist";
import { useRef, useState } from "react";
import { Trash } from "tabler-icons-react";

export default function CategoryAddModal({
  setOpened,
  total,
  setTotal,
  activePage,
  setActivePage,
}) {
  const [activeTab, setActiveTab] = useState(tabList[0].value);
  const [mainCategoryFile, setMainCategoryFile] = useState(null);
  const [subCategoryFiles, setSubCategoryFiles] = useState([]);
  const fileInputRef = useRef(null);
  const subCategoryFileRefs = useRef([]);

  const form = useForm({
    initialValues: {
      name: "",
      image: "",
      children: [],
    },
  });

  const imagePreview = (imageFile) => {
    const imageUrl = URL.createObjectURL(imageFile);
    return (
      <img
        src={imageUrl}
        width="130"
        alt=""
        onLoad={() => URL.revokeObjectURL(imageUrl)}
      />
    );
  };

  const handleMainFileChange = (event) => {
    const files = Array.from(event.target.files);
    setMainCategoryFile(files[0]);
    form.setFieldValue("image", files[0]);
  };

  const handleSubCategoryFileChange = (event, index) => {
    const files = Array.from(event.target.files);
    const newSubCategoryFiles = [...subCategoryFiles];
    newSubCategoryFiles[index] = files[0];
    setSubCategoryFiles(newSubCategoryFiles);
    form.setFieldValue(`children.${index}.image`, files[0]);
  };

  const handleFields = () => {
    return form.values.children.map((item, index) => (
      <Grid key={index} align="center">
        {/* Subcategory name */}
        <Grid.Col span={3}>
          <TextInput
            placeholder="Subcategory Name"
            required
            label={`Subcategory ${index + 1}`}
            sx={{ flex: 1 }}
            {...form.getInputProps(`children.${index}.name`)}
          />
        </Grid.Col>

        <Grid.Col span={3}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button
              onClick={() => subCategoryFileRefs.current[index].click()}
              variant="outline"
              leftIcon={<IconUpload size={16} />}
              style={{ marginTop: "25px" }}
            >
              Upload Image
            </Button>
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              ref={(el) => (subCategoryFileRefs.current[index] = el)}
              style={{ display: "none" }}
              onChange={(e) => handleSubCategoryFileChange(e, index)}
            />

            {/* Preview (small) */}
            {subCategoryFiles[index] && (
              <div style={{ marginTop: "25px" }}>
                {imagePreview(subCategoryFiles[index])}
              </div>
            )}
            <ActionIcon
              color="#ed522f"
              onClick={() => form.removeListItem("children", index)}
              style={{
                marginTop: "25px",
                padding: "2px",
                color: "red",
              }}
            >
              <Trash size={22} />
            </ActionIcon>
          </div>
        </Grid.Col>
      </Grid>
    ));
  };

  const [addCategory, { loading }] = useMutation(CREATE_CATEGORY, {
    update(cache, { data: { createCategory } }) {
      cache.updateQuery(
        {
          query: GET_CATEGORIES,
          variables: {
            first: parseInt(10),
            page: activePage,
            search: "",
          },
        },
        (data) => {
          if (data) {
            return {
              categories: {
                ...data.categories,
                data: [createCategory, ...data.categories.data],
              },
            };
          }
        },
      );
    },
  });

  const submit = () => {
    let variables = {
      name: form.getInputProps("name").value,
      image: mainCategoryFile,
      children: form.getInputProps("children").value.map((child, index) => ({
        ...child,
        image: subCategoryFiles[index],
      })),
    };
    console.log(variables);
    addCategory({
      variables,
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "Category Created Successfully",
        });
        setOpened(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to create Category ",
        });
      },
    });
  };

  const { height } = useViewportSize();

  return (
    <Tabs color="blue" value={activeTab} onTabChange={setActiveTab}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />

      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())}>
          <Grid>
            <Grid.Col span={3}>
              <TextInput
                required
                label="Category Name"
                placeholder="Enter Category name"
                {...form.getInputProps("name")}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Button
                onClick={() => fileInputRef.current.click()}
                variant="outline"
                leftIcon={<IconUpload size={16} />}
                style={{ marginTop: "25px" }}
              >
                Upload Image
              </Button>
              <input
                type="file"
                accept={IMAGE_MIME_TYPE}
                ref={fileInputRef}
                style={{ display: "none" }} // Hide the file input
                onChange={handleMainFileChange} // Handle main image selection
              />
              <span style={{ marginLeft: "15px" }}>
                {" "}
                {mainCategoryFile && imagePreview(mainCategoryFile)}
              </span>
            </Grid.Col>
            <Grid.Col span={12}>
              {handleFields()}
              <Group position="start" mt="md">
                <Button
                  color="blue"
                  variant="outline"
                  fullWidth
                  style={{
                    width: "200px",
                  }}
                  onClick={() => {
                    form.insertListItem("children", {
                      name: "",
                      image: "",
                    });
                  }}
                >
                  Add New Subcategory
                </Button>
              </Group>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button
                style={{
                  width: "25%",
                  marginTop: "15px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
                type="submit"
                color="blue"
                fullWidth
              >
                Submit
              </Button>
            </Grid.Col>
          </Grid>
        </form>
      </ScrollArea>
    </Tabs>
  );
}
